import type { UseQueryResult } from "@tanstack/react-query";
import { AlertTriangle, Brain, Loader2, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { PatternInsight, PatternsResponse } from "../../lib/api.js";

const SEVERITY_COLOR: Record<PatternInsight["severity"], string> = {
  critical: "var(--risk-high)",
  warning: "var(--risk-medium)",
  info: "var(--accent)",
};

const SEVERITY_LABEL_KEY: Record<PatternInsight["severity"], string> = {
  critical: "cockpit.patterns.severity.critical",
  warning: "cockpit.patterns.severity.warning",
  info: "cockpit.patterns.severity.info",
};

// ── Zone 4: KI-Musteranalyse ──────────────────────────────────────────────────

export function PatternPanel(props: { query: UseQueryResult<PatternsResponse> }) {
  const { t } = useTranslation();
  const { data: aiData, isLoading: aiLoading, isError: aiError } = props.query;

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain size={15} style={{ color: "var(--accent)" }} />
          <h2 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
            {t("cockpit.patterns.title")}
          </h2>
        </div>
        {aiData && (
          <div className="flex items-center gap-2">
            {aiData.trendDelta !== 0 ? (
              <span
                className="flex items-center gap-1 text-xs font-mono-data font-medium"
                style={{
                  color: aiData.trendDelta > 0 ? "var(--risk-high)" : "var(--risk-low)",
                }}
              >
                {aiData.trendDelta > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {aiData.trendDelta > 0 ? "+" : ""}
                {aiData.trendDelta}pp
              </span>
            ) : (
              <span
                className="flex items-center gap-1 text-xs font-mono-data"
                style={{ color: "var(--text-muted)" }}
              >
                <Minus size={12} /> ±0pp
              </span>
            )}
            <span
              className="text-xs px-1.5 py-0.5 rounded"
              style={{
                background: "color-mix(in srgb, var(--accent) 9%, transparent)",
                color: "var(--accent)",
              }}
            >
              {t("cockpit.patterns.vsLastWeek")}
            </span>
          </div>
        )}
      </div>

      {aiLoading && (
        <div className="flex items-center gap-2 py-8 justify-center">
          <Loader2 size={16} className="animate-spin" style={{ color: "var(--accent)" }} />
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            {t("cockpit.patterns.loading")}
          </span>
        </div>
      )}

      {aiError && !aiLoading && (
        <div
          className="flex items-center gap-2 py-6 px-3 rounded"
          style={{ background: "color-mix(in srgb, var(--risk-high) 6%, transparent)" }}
        >
          <AlertTriangle size={14} style={{ color: "var(--risk-high)" }} />
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            {t("cockpit.patterns.error")}
          </span>
        </div>
      )}

      {aiData && (
        <>
          <p className="text-xs mb-4 leading-relaxed" style={{ color: "var(--text-muted)" }}>
            {aiData.summary}
          </p>

          {aiData.earlyWarnings.length > 0 && (
            <div
              className="flex flex-wrap gap-2 mb-4 p-3 rounded"
              style={{
                background: "color-mix(in srgb, var(--risk-medium) 6%, transparent)",
                border: "1px solid color-mix(in srgb, var(--risk-medium) 19%, transparent)",
              }}
            >
              <span
                className="text-xs font-semibold w-full"
                style={{ color: "var(--risk-medium)" }}
              >
                {t("cockpit.patterns.earlyWarnings")}
              </span>
              {aiData.earlyWarnings.map((w) => (
                <span
                  key={w}
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    background: "color-mix(in srgb, var(--risk-medium) 13%, transparent)",
                    color: "var(--risk-medium)",
                  }}
                >
                  {w}
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {aiData.insights.map((insight) => {
              const color = SEVERITY_COLOR[insight.severity];
              return (
                <div
                  key={insight.id}
                  className="rounded p-4"
                  style={{
                    background: `color-mix(in srgb, ${color} 3%, transparent)`,
                    border: `1px solid color-mix(in srgb, ${color} 19%, transparent)`,
                  }}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold" style={{ color }}>
                      {insight.title}
                    </span>
                    <span
                      className="text-xs px-1.5 py-0.5 rounded"
                      style={{
                        background: `color-mix(in srgb, ${color} 13%, transparent)`,
                        color,
                      }}
                    >
                      {t(SEVERITY_LABEL_KEY[insight.severity])}
                    </span>
                  </div>
                  <p
                    className="text-xs leading-relaxed mb-2"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {insight.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                      {insight.cohort} ·{" "}
                      {t("cockpit.patterns.affected", { count: insight.affectedCount })}
                    </span>
                    <span className="font-mono-data text-xs" style={{ color: "var(--text-muted)" }}>
                      {t("cockpit.patterns.confidence", {
                        pct: (insight.confidence * 100).toFixed(0),
                      })}
                    </span>
                  </div>
                  <div
                    className="mt-2 pt-2 text-xs"
                    style={{
                      borderTop: `1px solid color-mix(in srgb, ${color} 13%, transparent)`,
                      color: "var(--text-secondary)",
                    }}
                  >
                    → {insight.recommendation}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
